import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import ModalConfirmar from '../ui/ModalConfirmar';
import { SelectFiltro } from '../ui/Filtros';
import { ESTADO_SOLICITUD_MOV } from '../ui/estados';

const ESTADOS = ['PENDIENTE', 'APROBADO', 'RECHAZADO', 'CANCELADO'];
const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

// Bandeja de cuatro ojos: los movimientos que esperan el visto bueno de una
// segunda persona. Mientras están aquí, el stock NO ha cambiado.
export default function Aprobaciones() {
  const { usuario } = useAuth();
  const [estado, setEstado] = useState('PENDIENTE');
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [accion, setAccion] = useState(null); // {tipo, solicitud}

  const cargar = useCallback((filtro) => {
    setCargando(true);
    setError(null);
    return api
      .get(`/aprobaciones?estado=${filtro}`)
      .then(setSolicitudes)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    let vigente = true;
    api
      .get(`/aprobaciones?estado=${estado}`)
      .then((d) => vigente && setSolicitudes(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [estado]);

  const columnas = [
    { etiqueta: 'Folio', valor: (s) => s.id, clase: 'font-mono text-xs' },
    { etiqueta: 'Movimiento', valor: (s) => `${s.tipo} ${Number(s.cantidad)} × ${s.sku}`, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Producto', valor: (s) => s.producto, clase: 'text-gray-500' },
    { etiqueta: 'Desde', valor: (s) => s.ubicacion_origen || '—', clase: 'text-gray-500' },
    {
      etiqueta: 'Valor', alineado: 'derecha', tipo: 'moneda',
      valor: (s) => Number(s.valor_estimado),
      render: (s) => pesos.format(s.valor_estimado),
      clase: 'tabular-nums',
    },
    { etiqueta: 'Pidió', valor: (s) => s.solicitante || '—' },
    {
      etiqueta: 'Cuándo', tipo: 'fecha_hora', valor: (s) => s.creado_en,
      render: (s) => (
        <span className="flex items-center gap-1.5">
          <TiempoRelativo valor={s.creado_en} />
          {s.caducada && (
            <span className="text-[10px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
              caducada
            </span>
          )}
        </span>
      ),
    },
    {
      etiqueta: 'Estado', valor: (s) => s.estado,
      render: (s) => <ChipEstado mapa={ESTADO_SOLICITUD_MOV} clave={s.estado} />,
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Salidas por aprobar"
        subtitulo="Salidas, ajustes y mermas grandes que esperan el visto bueno de una segunda persona antes de aplicarse."
        nota={
          <NotaExplicativa titulo="¿Por qué está esto aquí, y qué pasó con la mercancía?">
            Superó el umbral que fijó tu empresa y <strong>no se aplicó</strong>: la mercancía sigue
            en su lugar y el stock no ha cambiado. Quien lo pidió no puede autorizarlo. Al aprobar, el movimiento se ejecuta de verdad en ese momento; si mientras tanto
            alguien se llevó lo que se iba a sacar, la aprobación falla y lo avisa.
          </NotaExplicativa>
        }
        filtros={
          <SelectFiltro valor={estado} onChange={setEstado} opciones={ESTADOS} />
        }
        columnas={columnas}
        filas={solicitudes}
        cargando={cargando}
        error={error}
        onReintentar={() => cargar(estado)}
        exportar={{ nombre: `aprobaciones-${estado.toLowerCase()}`, columnas }}
        vacio={
          <EstadoVacio
            icono={ShieldCheck}
            titulo={estado === 'PENDIENTE' ? 'No hay nada esperando autorización' : 'Nada que mostrar'}
            descripcion={
              estado === 'PENDIENTE'
                ? 'Cuando un movimiento supere el umbral de tu empresa, aparecerá aquí.'
                : 'Cambia el filtro de estado para ver otras solicitudes.'
            }
          />
        }
        acciones={(s) =>
          s.estado !== 'PENDIENTE'
            ? []
            : s.id_solicitante === usuario?.id
              ? [{ etiqueta: 'Cancelar la mía', onClick: () => setAccion({ tipo: 'cancelar', s }), peligro: true }]
              : [
                  { etiqueta: 'Autorizar', onClick: () => setAccion({ tipo: 'aprobar', s }) },
                  { etiqueta: 'Rechazar', onClick: () => setAccion({ tipo: 'rechazar', s }), peligro: true },
                ]
        }
      />

      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}

      {accion && (
        <ModalAccion
          {...accion}
          onCerrar={() => setAccion(null)}
          onListo={() => { setAccion(null); cargar(estado); }}
        />
      )}
    </>
  );
}

function ModalAccion({ tipo, s, onCerrar, onListo }) {
  const config = {
    aprobar: {
      titulo: `Autorizar el folio ${s.id}`,
      descripcion: 'El movimiento se ejecuta ahora mismo y el stock cambia. Queda registrado que tú lo autorizaste.',
      etiqueta: 'Autorizar y aplicar',
      pedirMotivo: false,
      enviar: () => api.post(`/aprobaciones/${s.id}/aprobar`, {}),
    },
    rechazar: {
      titulo: `Rechazar el folio ${s.id}`,
      descripcion: 'El movimiento no se aplicará. El inventario nunca se tocó.',
      etiqueta: 'Rechazar',
      peligro: true,
      pedirMotivo: true,
      enviar: (motivo) => api.post(`/aprobaciones/${s.id}/rechazar`, { motivo }),
    },
    cancelar: {
      titulo: `Retirar tu solicitud ${s.id}`,
      descripcion: 'Retiras tu propia petición. El inventario nunca se tocó.',
      etiqueta: 'Retirar solicitud',
      peligro: true,
      pedirMotivo: true,
      enviar: (motivo) => api.post(`/aprobaciones/${s.id}/cancelar`, { motivo }),
    },
  }[tipo];

  return (
    <ModalConfirmar
      titulo={config.titulo}
      descripcion={config.descripcion}
      cambios={[
        { campo: 'Movimiento', antes: `${s.sku} · ${s.producto}`, despues: `${s.tipo} de ${Number(s.cantidad)}` },
        { campo: 'Valor', antes: 'en espera', despues: pesos.format(s.valor_estimado) },
        { campo: 'Lo pidió', antes: '—', despues: s.solicitante || '—' },
      ]}
      aviso={
        tipo === 'aprobar' && s.caducada
          ? 'Esta solicitud caducó: se pidió hace demasiado y el almacén ya no es el de entonces. El sistema la rechazará; pídele a quien la creó que la vuelva a levantar.'
          : null
      }
      pedirMotivo={config.pedirMotivo}
      peligro={config.peligro}
      etiquetaConfirmar={config.etiqueta}
      onConfirmar={async (motivo) => { await config.enviar(motivo); onListo(); }}
      onCerrar={onCerrar}
    />
  );
}
